import Swiper from "./swiper-bundle.esm.browser.min.js";
import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";

const badges = document.querySelector(".photoswipe > .card__badges");
const DESKTOP_LIGHTBOX_MIN = 900;
const desktopLightboxQuery = window.matchMedia(
  `(min-width: ${DESKTOP_LIGHTBOX_MIN}px)`,
);

function isProductPageGallery(gallery) {
  return !!gallery?.closest("gallery-section[data-product-page]");
}

function isPdpPrimaryGallery(gallery) {
  const section = gallery?.closest("gallery-section");
  return (
    section?.dataset.productPage === "" && !section.id.endsWith("2")
  );
}

function getGallerySliderBreakpoint(galleryEl) {
  const section = galleryEl?.closest("gallery-section");
  const configScript = section?.querySelector("script[data-configuration]");
  if (!configScript) return 1200;

  try {
    const config = JSON.parse(configScript.textContent);
    return Number(config.sliderEnabledBreakpoint) || 1200;
  } catch {
    return 1200;
  }
}

function isPdpLightboxEnabled(galleryEl) {
  return window.innerWidth < getGallerySliderBreakpoint(galleryEl);
}

function getPdpHeroSlide(galleryEl) {
  if (!galleryEl) return null;

  return (
    Array.from(galleryEl.querySelectorAll("[data-swiper-slide]")).find(
      (slide) => !isGallerySlideHidden(slide),
    ) || null
  );
}

function isPdpHeroSlide(slide, galleryEl) {
  if (!slide || !galleryEl) return false;
  return getPdpHeroSlide(galleryEl) === slide;
}

function isPdpLightboxSlide(slide, galleryEl) {
  if (!slide || isGallerySlideHidden(slide)) return false;
  if (isPdpLightboxEnabled(galleryEl)) return true;
  return !isPdpHeroSlide(slide, galleryEl);
}

function getPdpLightboxSlides(galleryEl) {
  return Array.from(galleryEl.querySelectorAll("[data-swiper-slide]")).filter(
    (slide) => isPdpLightboxSlide(slide, galleryEl),
  );
}

function getPdpLightboxSlideIndex(galleryEl, slideEl) {
  return getPdpLightboxSlides(galleryEl).indexOf(slideEl);
}

function getPdpLightboxLinks(galleryEl) {
  return getPdpLightboxSlides(galleryEl)
    .map((slide) => slide.querySelector("a[href]"))
    .filter(Boolean);
}

function isProductPageLightboxDisabled(gallery) {
  return !!gallery?.closest(".hz__home-preview");
}

function isGallerySlideHidden(slide) {
  if (!slide) return false;
  if (slide.style.display === "none") return true;
  const style = window.getComputedStyle(slide);
  return style.display === "none" || style.visibility === "hidden";
}

function getLargestSrcsetUrl(img) {
  const srcset = img?.getAttribute("srcset");
  if (!srcset) return "";

  const candidates = srcset
    .split(",")
    .map((part) => {
      const bits = part.trim().split(/\s+/);
      return {
        url: bits[0],
        width: parseInt(bits[1], 10) || 0,
      };
    })
    .sort((a, b) => b.width - a.width);

  return candidates[0]?.url || "";
}

function getShopifyAssetMasterUrl(url) {
  if (!url) return "";
  return url.replace(/_(\d+)x(?=\.[a-z]{3,4}(?:\?|$))/i, "");
}

function upgradeShopifyCdnImageUrl(url, targetWidth = 3840) {
  if (!url) return "";

  const masterAssetUrl = getShopifyAssetMasterUrl(url);
  if (masterAssetUrl !== url) return masterAssetUrl;

  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.searchParams.has("width")) return url;

    const currentWidth = parseInt(parsed.searchParams.get("width"), 10) || 0;
    if (currentWidth >= targetWidth) return url;

    parsed.searchParams.set("width", String(targetWidth));
    return parsed.toString();
  } catch {
    return url;
  }
}

function applyLightboxImageSharpCap(img, viewport) {
  const capToSourceResolution = () => {
    if (!img?.naturalWidth || !viewport) return;
    if (img.classList.contains("hz-pdp-lightbox__img--cover")) return;

    const dpr = window.devicePixelRatio || 1;
    const maxSharpCssWidth = Math.floor(img.naturalWidth / dpr);
    const viewportWidth = viewport.offsetWidth;

    if (maxSharpCssWidth > 0 && maxSharpCssWidth < viewportWidth) {
      img.style.width = `${maxSharpCssWidth}px`;
      img.style.height = "auto";
    }
  };

  if (img.complete) {
    capToSourceResolution();
  } else {
    img.addEventListener("load", capToSourceResolution, { once: true });
  }
}

function getHzPdpLightboxImageSrc(img, link) {
  const explicit = img?.dataset?.hzLightboxSrc || link?.dataset?.hzLightboxSrc || "";
  if (explicit) return upgradeShopifyCdnImageUrl(explicit);

  const linkHref = link?.getAttribute("href") || "";
  if (linkHref && !/_\d+x\./i.test(linkHref)) {
    return upgradeShopifyCdnImageUrl(linkHref);
  }

  const largestSrcset = getLargestSrcsetUrl(img);
  if (largestSrcset) return upgradeShopifyCdnImageUrl(largestSrcset);

  const current = img?.currentSrc || img?.src || linkHref || "";
  return upgradeShopifyCdnImageUrl(getShopifyAssetMasterUrl(current) || current);
}

const GALLERY_EXPAND_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

const HZ_PDP_LIGHTBOX_ARROW =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M11 4L6 9L11 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const hzPdpLightbox = {
  root: null,
  slides: [],
  index: 0,
  isOpen: false,
  dragOffset: 0,
  isDragging: false,
  swipeAxis: null,
  touchStartX: 0,
  touchStartY: 0,

  ensureRoot() {
    if (this.root) return this.root;

    this.root = document.createElement("div");
    this.root.className = "hz-pdp-lightbox";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="hz-pdp-lightbox__backdrop" data-hz-pdp-lightbox-close></div>
      <button type="button" class="hz-pdp-lightbox__close" aria-label="Close" data-hz-pdp-lightbox-close></button>
      <button type="button" class="hz-pdp-lightbox__arrow hz-pdp-lightbox__arrow--prev" aria-label="Previous">${HZ_PDP_LIGHTBOX_ARROW}</button>
      <button type="button" class="hz-pdp-lightbox__arrow hz-pdp-lightbox__arrow--next" aria-label="Next">${HZ_PDP_LIGHTBOX_ARROW}</button>
      <div class="hz-pdp-lightbox__stage">
        <div class="hz-pdp-lightbox__viewport">
          <div class="hz-pdp-lightbox__track"></div>
        </div>
      </div>
      <div class="hz-pdp-lightbox__dots" role="tablist"></div>
    `;
    document.body.appendChild(this.root);

    this.stage = this.root.querySelector(".hz-pdp-lightbox__stage");
    this.viewport = this.root.querySelector(".hz-pdp-lightbox__viewport");
    this.track = this.root.querySelector(".hz-pdp-lightbox__track");
    this.dots = this.root.querySelector(".hz-pdp-lightbox__dots");
    this.prevBtn = this.root.querySelector(".hz-pdp-lightbox__arrow--prev");
    this.nextBtn = this.root.querySelector(".hz-pdp-lightbox__arrow--next");

    this.root.querySelectorAll("[data-hz-pdp-lightbox-close]").forEach((el) => {
      el.addEventListener("click", () => this.close());
    });
    this.prevBtn.addEventListener("click", () => this.go(-1));
    this.nextBtn.addEventListener("click", () => this.go(1));

    this.bindSwipe(this.viewport);

    this._onKeydown = (event) => {
      if (!this.isOpen) return;
      if (event.key === "Escape") this.close();
      if (event.key === "ArrowLeft") this.go(-1);
      if (event.key === "ArrowRight") this.go(1);
    };
    document.addEventListener("keydown", this._onKeydown);

    return this.root;
  },

  bindSwipe(element) {
    element.addEventListener(
      "touchstart",
      (event) => {
        if (!this.isOpen || this.slides.length <= 1) return;

        const touch = event.touches[0];
        if (!touch) return;

        this.isDragging = true;
        this.swipeAxis = null;
        this.dragOffset = 0;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.updateTrackPosition(false);
      },
      { passive: true },
    );

    element.addEventListener(
      "touchmove",
      (event) => {
        if (!this.isDragging || !this.isOpen) return;

        const touch = event.touches[0];
        if (!touch) return;

        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        if (!this.swipeAxis) {
          if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
          this.swipeAxis =
            Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        }

        if (this.swipeAxis !== "horizontal") return;

        event.preventDefault();
        this.dragOffset = deltaX;
        this.updateTrackPosition(false);
      },
      { passive: false },
    );

    const finishSwipe = (event) => {
      if (!this.isDragging || !this.isOpen) return;

      const touch = event.changedTouches[0];
      const wasHorizontal = this.swipeAxis === "horizontal";
      const deltaX = touch ? touch.clientX - this.touchStartX : this.dragOffset;
      const viewportWidth = this.viewport?.offsetWidth || 0;
      const threshold = Math.max(48, viewportWidth * 0.18);

      this.isDragging = false;
      this.swipeAxis = null;
      this.dragOffset = 0;

      if (wasHorizontal && Math.abs(deltaX) >= threshold) {
        this.go(deltaX > 0 ? -1 : 1);
        return;
      }

      this.updateTrackPosition(true);
    };

    element.addEventListener("touchend", finishSwipe, { passive: true });
    element.addEventListener("touchcancel", finishSwipe, { passive: true });
  },

  open(slides, index = 0) {
    if (!slides.length) return;

    this.ensureRoot();
    this.slides = slides;
    this.index = Math.max(0, Math.min(index, slides.length - 1));
    this.renderTrack();
    this.renderDots();

    this.root.hidden = false;
    this.isOpen = true;
    document.body.classList.add("hz-pdp-lightbox-open");
    badges?.classList.add("hide");

    // Viewport has no width while [hidden]; measure after the lightbox is visible.
    this.updateTrackPosition(false);
    requestAnimationFrame(() => {
      if (!this.isOpen) return;
      this.updateTrackPosition(false);
    });
  },

  close() {
    if (!this.root) return;

    this.isDragging = false;
    this.swipeAxis = null;
    this.dragOffset = 0;
    this.root.hidden = true;
    this.isOpen = false;
    document.body.classList.remove("hz-pdp-lightbox-open");
    badges?.classList.remove("hide");
  },

  go(delta, animate = true) {
    if (this.slides.length <= 1) return;
    this.index = (this.index + delta + this.slides.length) % this.slides.length;
    this.updateDots();
    this.updateTrackPosition(animate);
  },

  renderTrack() {
    if (!this.track) return;

    this.track.innerHTML = "";
    this.slides.forEach((slide) => {
      const slideEl = document.createElement("div");
      slideEl.className = "hz-pdp-lightbox__slide";

      const img = document.createElement("img");
      img.className = slide.contain
        ? "hz-pdp-lightbox__img hz-pdp-lightbox__img--contain"
        : "hz-pdp-lightbox__img hz-pdp-lightbox__img--cover";
      img.src = slide.src;
      img.alt = slide.alt || "";
      img.decoding = "sync";
      img.draggable = false;
      if (slide.contain) {
        applyLightboxImageSharpCap(img, this.viewport);
      }

      slideEl.appendChild(img);
      this.track.appendChild(slideEl);
    });
  },

  updateTrackPosition(animate = true) {
    if (!this.track || !this.viewport) return;

    const slideWidth = this.viewport.offsetWidth;
    const offset = -(this.index * slideWidth) + this.dragOffset;

    this.track.classList.toggle("is-animating", animate);
    this.track.style.transform = `translate3d(${Math.round(offset)}px, 0, 0)`;
  },

  renderDots() {
    this.dots.innerHTML = "";
    this.slides.forEach((_, dotIndex) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "hz-pdp-lightbox__dot";
      dot.setAttribute("role", "tab");
      dot.setAttribute(
        "aria-label",
        `View image ${dotIndex + 1} of ${this.slides.length}`,
      );
      dot.addEventListener("click", () => {
        this.index = dotIndex;
        this.updateDots();
        this.updateTrackPosition(true);
      });
      this.dots.appendChild(dot);
    });
  },

  updateDots() {
    this.dots.querySelectorAll(".hz-pdp-lightbox__dot").forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.index;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    const oneSlide = this.slides.length <= 1;
    this.prevBtn.hidden = oneSlide;
    this.nextBtn.hidden = oneSlide;
    this.dots.hidden = oneSlide;
  },
};

function buildHzPdpLightboxSlides(links) {
  return links
    .map((link) => {
      const linkImg = link.querySelector("img.wt-product__img");
      const gallerySlide = link.closest("[data-swiper-slide]");
      return {
        src: getHzPdpLightboxImageSrc(linkImg, link),
        alt: linkImg?.getAttribute("alt") || "",
        contain:
          gallerySlide?.classList.contains("hz-gallery-slide--theme-asset") ||
          gallerySlide?.classList.contains("hz-gallery-slide--benefits") ||
          gallerySlide?.dataset?.mediaId?.startsWith("custom-gallery-"),
      };
    })
    .filter((slide) => slide.src);
}

function syncZoomLinkDimensions(link, img) {
  if (!link || !img) return;

  let width =
    img.naturalWidth ||
    parseInt(img.getAttribute("width"), 10) ||
    parseInt(link.dataset.pswpWidth, 10) ||
    0;
  let height =
    img.naturalHeight ||
    parseInt(img.getAttribute("height"), 10) ||
    parseInt(link.dataset.pswpHeight, 10) ||
    0;

  if (!width || !height) {
    const ratio =
      img.width && img.height
        ? img.width / img.height
        : width && height
          ? width / height
          : 1;
    width = width || 1200;
    height = height || Math.round(width / ratio);
  }

  link.dataset.pswpWidth = String(width);
  link.dataset.pswpHeight = String(height);
  link.href = getHzPdpLightboxImageSrc(img, link);
}

function waitForGalleryImage(img) {
  if (!img) return Promise.resolve();
  if (img.complete && img.naturalWidth) return Promise.resolve();

  return new Promise((resolve) => {
    img.addEventListener("load", resolve, { once: true });
    img.addEventListener("error", resolve, { once: true });
  });
}

function ensureZoomLink(slide, img) {
  let link = slide.querySelector("a[href]");
  if (!link) {
    link = document.createElement("a");
    link.href = getHzPdpLightboxImageSrc(img, null);
    link.setAttribute("target", "_blank");
    link.setAttribute("tabindex", "-1");
    img.parentNode.insertBefore(link, img);
    link.appendChild(img);
  }
  syncZoomLinkDimensions(link, img);
  return link;
}

function getVisibleGalleryZoomLinks(galleryEl) {
  return Array.from(galleryEl.querySelectorAll("a[href]")).filter((link) => {
    const slide = link.closest("[data-swiper-slide]");
    return slide && !isGallerySlideHidden(slide);
  });
}

function buildLightboxDataSource(links, isMobileCrop = false) {
  const { stageSize } = getHzPdpStageLayout();

  return links.map((link) => {
    const linkImg = link.querySelector("img.wt-product__img");
    const src = getHzPdpLightboxImageSrc(linkImg, link);
    const alt = linkImg?.getAttribute("alt") || "";

    if (linkImg) syncZoomLinkDimensions(link, linkImg);

    const naturalWidth = parseInt(link.dataset.pswpWidth, 10) || 1200;
    const naturalHeight = parseInt(link.dataset.pswpHeight, 10) || 1200;

    if (isMobileCrop && src) {
      return {
        src,
        msrc: getHzPdpLightboxImageSrc(linkImg, link) || src,
        alt,
        w: stageSize,
        h: stageSize,
        width: stageSize,
        height: stageSize,
      };
    }

    return {
      src,
      width: naturalWidth,
      height: naturalHeight,
      w: naturalWidth,
      h: naturalHeight,
      msrc: getHzPdpLightboxImageSrc(linkImg, link) || src,
      alt,
    };
  }).filter((item) => item.src);
}

async function openProductPageLightbox(gallerySection, slideEl) {
  const galleryEl = gallerySection.elements?.gallery;
  if (!galleryEl || !isPdpLightboxSlide(slideEl, galleryEl)) return;

  galleryEl.classList.add("photoswipe");

  const img = slideEl.querySelector("img.wt-product__img");
  if (img) {
    await waitForGalleryImage(img);
    ensureZoomLink(slideEl, img);
  }

  syncPhotoSwipeLinks(galleryEl);

  const lightboxSlides = getPdpLightboxSlides(galleryEl);
  let index = getPdpLightboxSlideIndex(galleryEl, slideEl);
  if (index < 0) {
    const slideLink = slideEl.querySelector("a[href]");
    const links = getPdpLightboxLinks(galleryEl);
    index = slideLink ? links.indexOf(slideLink) : -1;
  }
  if (index < 0) return;

  const links = getPdpLightboxLinks(galleryEl);
  const slides = buildHzPdpLightboxSlides(links);
  if (!slides.length) return;
  hzPdpLightbox.open(slides, index);
}

const HZ_PSWP_SIDE_PADDING = 20;

let hzPdpMobileLightbox = false;

function getHzPdpStageLayout() {
  const maxStage = Math.min(
    window.innerWidth - HZ_PSWP_SIDE_PADDING * 2,
    window.innerHeight - 120,
  );
  const stageSize = Math.max(260, Math.floor(maxStage));
  const verticalPad = Math.max(48, Math.round((window.innerHeight - stageSize) / 2));

  return {
    stageSize,
    padding: {
      top: verticalPad,
      bottom: verticalPad,
      left: HZ_PSWP_SIDE_PADDING,
      right: HZ_PSWP_SIDE_PADDING,
    },
  };
}

function normalizeHzPdpSlideCrop(pswp) {
  if (!pswp?.element?.classList.contains("pswp--hz-pdp-mobile")) return;

  const { stageSize } = getHzPdpStageLayout();
  pswp.element.style.setProperty("--hz-pswp-stage-size", `${stageSize}px`);

  (pswp.mainScroll?.itemHolders || []).forEach((holder) => {
    const wrap = holder?.el?.querySelector(".pswp__zoom-wrap");
    const img = holder?.el?.querySelector(".pswp__img");
    if (!wrap || !img || !img.src) return;

    const applyCrop = () => {
      wrap.style.overflow = "hidden";
      wrap.style.borderRadius = "8px";
      img.style.objectFit = "cover";
      img.style.objectPosition = "center center";
    };

    if (img.complete) {
      applyCrop();
    } else {
      img.addEventListener("load", applyCrop, { once: true });
    }
  });
}

function applyHzPdpUniformCrop(pswp) {
  normalizeHzPdpSlideCrop(pswp);
}

function getHzPdpZoomWrap(pswp) {
  return pswp?.currSlide?.container?.querySelector(".pswp__zoom-wrap");
}

function getHzPdpImageRect(pswp) {
  const wrap = getHzPdpZoomWrap(pswp);
  if (wrap) {
    const rect = wrap.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        midY: (rect.top + rect.bottom) / 2,
      };
    }
  }

  const { stageSize } = getHzPdpStageLayout();
  const verticalPad = Math.max(48, Math.round((window.innerHeight - stageSize) / 2));
  const left = HZ_PSWP_SIDE_PADDING;
  return {
    top: verticalPad,
    bottom: verticalPad + stageSize,
    left,
    right: left + stageSize,
    midY: verticalPad + stageSize / 2,
  };
}

function positionHzPdpLightboxChrome(pswp) {
  if (!pswp?.element?.classList.contains("pswp--hz-pdp-mobile")) return;

  const imageRect = getHzPdpImageRect(pswp);
  if (!imageRect) return;

  const root = pswp.element;
  const arrowSize = 36;
  const arrowOverlap = 18;
  const edgeInset = 8;

  root.style.setProperty("--hz-pswp-image-mid-y", `${imageRect.midY}px`);
  root.style.setProperty("--hz-pswp-image-left", `${imageRect.left}px`);
  root.style.setProperty("--hz-pswp-image-right", `${imageRect.right}px`);

  applyHzPdpUniformCrop(pswp);

  const prevBtn = root.querySelector(".pswp__button--arrow--prev");
  const nextBtn = root.querySelector(".pswp__button--arrow--next");
  if (prevBtn) {
    prevBtn.style.top = `${imageRect.midY}px`;
    prevBtn.style.left = `${Math.max(edgeInset, imageRect.left - arrowOverlap)}px`;
    prevBtn.style.right = "auto";
    prevBtn.style.transform = "translateY(-50%)";
  }
  if (nextBtn) {
    const nextLeft = imageRect.right - arrowOverlap;
    nextBtn.style.top = `${imageRect.midY}px`;
    nextBtn.style.left = `${Math.min(window.innerWidth - arrowSize - edgeInset, nextLeft)}px`;
    nextBtn.style.right = "auto";
    nextBtn.style.transform = "translateY(-50%)";
  }

  const closeBtn = root.querySelector(".pswp__button--close");
  if (closeBtn) {
    closeBtn.style.top = `${Math.max(12, imageRect.top - 12)}px`;
    closeBtn.style.right = `${Math.max(12, window.innerWidth - imageRect.right - 12)}px`;
  }

  const pagination = root.querySelector(".hz-pswp-pagination");
  if (pagination) {
    pagination.style.top = `${imageRect.bottom - 22}px`;
  }
}

function updateHzPdpPaginationState(pswp) {
  pswp.element
    ?.querySelectorAll(".hz-pswp-pagination__dot")
    .forEach((dot, index) => {
      dot.classList.toggle("is-active", index === pswp.currIndex);
    });
}

function initHzPdpMobileLightbox(pswp) {
  if (!pswp?.element || pswp.element.classList.contains("pswp--hz-pdp-mobile")) {
    updateHzPdpPaginationState(pswp);
    positionHzPdpLightboxChrome(pswp);
    return;
  }

  pswp.element.classList.add("pswp--hz-pdp-mobile");

  let pagination = pswp.element.querySelector(".hz-pswp-pagination");
  if (!pagination) {
    pagination = document.createElement("div");
    pagination.className = "hz-pswp-pagination pswp__hide-on-close";
    pswp.element.appendChild(pagination);
  }

  pagination.innerHTML = "";
  for (let index = 0; index < pswp.numItems; index += 1) {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.className = "hz-pswp-pagination__dot";
    dot.setAttribute(
      "aria-label",
      `View image ${index + 1} of ${pswp.numItems}`,
    );
    if (index === pswp.currIndex) dot.classList.add("is-active");
    dot.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      pswp.goTo(index);
    });
    pagination.appendChild(dot);
  }

  pswp._hzPdpPositionHandler = () => positionHzPdpLightboxChrome(pswp);
  pswp.on("change", pswp._hzPdpPositionHandler);
  pswp.on("zoomPanUpdate", pswp._hzPdpPositionHandler);
  pswp.on("openingAnimationEnd", pswp._hzPdpPositionHandler);
  pswp.on("loadComplete", pswp._hzPdpPositionHandler);
  window.addEventListener("resize", pswp._hzPdpPositionHandler);
  requestAnimationFrame(() => positionHzPdpLightboxChrome(pswp));
  setTimeout(() => positionHzPdpLightboxChrome(pswp), 100);
}

function destroyHzPdpMobileLightbox(pswp) {
  if (pswp?._hzPdpPositionHandler) {
    pswp.off("change", pswp._hzPdpPositionHandler);
    pswp.off("zoomPanUpdate", pswp._hzPdpPositionHandler);
    pswp.off("openingAnimationEnd", pswp._hzPdpPositionHandler);
    pswp.off("loadComplete", pswp._hzPdpPositionHandler);
    window.removeEventListener("resize", pswp._hzPdpPositionHandler);
    pswp._hzPdpPositionHandler = null;
  }

  pswp?.element?.classList.remove("pswp--hz-pdp-mobile");
  pswp?.element?.querySelector(".hz-pswp-pagination")?.remove();
  pswp?.element?.querySelector(".hz-pswp-frame")?.remove();

  const closeBtn = pswp?.element?.querySelector(".pswp__button--close");
  if (closeBtn) {
    closeBtn.style.top = "";
    closeBtn.style.right = "";
  }

  pswp?.element
    ?.querySelectorAll(".pswp__button--arrow--prev, .pswp__button--arrow--next")
    .forEach((button) => {
      button.style.top = "";
      button.style.left = "";
      button.style.right = "";
      button.style.transform = "";
    });

  hzPdpMobileLightbox = false;
}

/** Keep PhotoSwipe in sync with flavour-filtered / CSS-hidden gallery slides. */
function syncPhotoSwipeLinks(root = document) {
  const galleries =
    root instanceof Element && root.matches(".photoswipe[data-gallery]")
      ? [root]
      : Array.from(root.querySelectorAll(".photoswipe[data-gallery]"));

  galleries.forEach((gallery) => {
    const ignoreForPhotoSwipe =
      isPdpPrimaryGallery(gallery) || isProductPageLightboxDisabled(gallery);

    gallery.querySelectorAll("a[href]").forEach((link) => {
      const slide = link.closest("[data-swiper-slide]");
      link.toggleAttribute(
        "data-pswp-ignore",
        ignoreForPhotoSwipe ||
          (slide ? isGallerySlideHidden(slide) : false),
      );
    });
  });
}

const lightbox = new PhotoSwipeLightbox({
  // may select multiple "galleries"
  gallery: ".photoswipe",

  // Elements within gallery (slides) — skip hidden flavour slots
  children: "a:not([data-pswp-ignore])",

  // setup PhotoSwipe Core dynamic import
  pswpModule: () => import("./photoswipe.esm.min.js"),

  bgOpacity: 1,
});
lightbox.addFilter("clickedIndex", (index, event) => {
  if (
    index >= 0 &&
    isProductPageLightboxDisabled(event.currentTarget)
  ) {
    return -1;
  }
  return index;
});
lightbox.addFilter("options", (options) => {
  if (!hzPdpMobileLightbox) return options;

  const { padding } = getHzPdpStageLayout();

  return {
    ...options,
    padding,
    loop: true,
    bgOpacity: 0.55,
    showHideAnimationType: "fade",
    initialZoomLevel: "fill",
    maxZoomLevel: 4,
    secondaryZoomLevel: 2,
  };
});
lightbox.addFilter("itemData", (itemData) => {
  if (itemData.w && itemData.h) return itemData;

  const width = itemData.width || itemData.w || 1200;
  const height = itemData.height || itemData.h || 1200;

  return {
    ...itemData,
    w: width,
    h: height,
    width,
    height,
  };
});
lightbox.addFilter("domItemData", (itemData, element) => {
  if (itemData.w && itemData.h) return itemData;

  const img = element.querySelector("img");
  if (img) {
    syncZoomLinkDimensions(element, img);
    itemData.w = parseInt(element.dataset.pswpWidth, 10) || 1200;
    itemData.h = parseInt(element.dataset.pswpHeight, 10) || 1200;
    itemData.width = itemData.w;
    itemData.height = itemData.h;
  }

  if (!itemData.w || !itemData.h) {
    itemData.w = itemData.w || 1200;
    itemData.h = itemData.h || 1200;
    itemData.width = itemData.w;
    itemData.height = itemData.h;
  }

  return itemData;
});
lightbox.init();
syncPhotoSwipeLinks();
desktopLightboxQuery.addEventListener("change", () => syncPhotoSwipeLinks());
document.addEventListener("hz:sync-photoswipe", () => syncPhotoSwipeLinks());
document.addEventListener(
  "click",
  (event) => {
    const gallery = event.target.closest(".photoswipe[data-gallery]");
    if (!gallery) return;

    syncPhotoSwipeLinks(gallery);

    if (
      isProductPageLightboxDisabled(gallery) &&
      event.target.closest("a[href]")
    ) {
      event.preventDefault();
    }
  },
  true,
);
lightbox.on("beforeOpen", () => {
  syncPhotoSwipeLinks();

  // PDP mobile uses hz-pdp-lightbox; desktop uses standard PhotoSwipe (no crop mode).
  hzPdpMobileLightbox = false;

  badges?.classList.add("hide");
});

lightbox.on("change", () => {
  if (!hzPdpMobileLightbox || !lightbox.pswp) return;
  initHzPdpMobileLightbox(lightbox.pswp);
  updateHzPdpPaginationState(lightbox.pswp);
  normalizeHzPdpSlideCrop(lightbox.pswp);
});

lightbox.on("contentLoad", () => {
  if (!hzPdpMobileLightbox || !lightbox.pswp) return;
  normalizeHzPdpSlideCrop(lightbox.pswp);
  positionHzPdpLightboxChrome(lightbox.pswp);
});

lightbox.on("contentLoadImage", () => {
  if (!hzPdpMobileLightbox || !lightbox.pswp) return;
  normalizeHzPdpSlideCrop(lightbox.pswp);
  positionHzPdpLightboxChrome(lightbox.pswp);
});

lightbox.on("destroy", () => {
  destroyHzPdpMobileLightbox(lightbox.pswp);
  badges?.classList.remove("hide");
});

if (!customElements.get("gallery-section")) {
  customElements.define(
    "gallery-section",
    class GallerySection extends HTMLElement {
      matchAutoHeightBand() {
        const layout = this.configuration.desktopLayout;
        if (layout === "collage" || layout === "masonry") {
          return this.matchResolution();
        }
        return window.innerWidth <= 900 ? "auto" : "fixed";
      }

      getSliderBreakpoint() {
        return Number(this.configuration.sliderEnabledBreakpoint) || 900;
      }

      isMobileGallery() {
        return window.innerWidth < this.getSliderBreakpoint();
      }

      isMobileCollageCarousel() {
        return (
          this.isProductPage &&
          this.isMobileGallery() &&
          !this.id.endsWith("2") &&
          this.configuration.desktopLayout === "collage"
        );
      }

      resetGalleryInlineDimensions() {
        const gallery = this.elements?.gallery;
        if (!gallery) return;

        gallery.style.removeProperty("height");
        gallery.style.removeProperty("min-height");

        const wrapper = gallery.querySelector("[data-swiper-container]");
        if (wrapper) wrapper.style.removeProperty("height");

        gallery.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
          slide.style.removeProperty("width");
          slide.style.removeProperty("height");
          slide.style.removeProperty("margin-right");
          slide.style.removeProperty("margin-bottom");
        });
      }

      refreshSwiperDimensions() {
        if (this.isMobileCollageCarousel()) {
          this.resetGalleryInlineDimensions();
        }

        if (this.gallerySwiper) {
          if (
            this.isProductPage &&
            this.isMobileGallery() &&
            this.gallerySwiper.wrapperEl
          ) {
            this.gallerySwiper.wrapperEl.style.height = "";
          }
          this.gallerySwiper.updateSize();
          this.gallerySwiper.update();
          if (this.gallerySwiper.params?.autoHeight) {
            this.gallerySwiper.updateAutoHeight(0);
          }
        }

        if (this.thumbsSwiper) {
          this.thumbsSwiper.updateSize();
          this.thumbsSwiper.update();
          if (this.thumbsSwiper.params?.autoHeight) {
            this.thumbsSwiper.updateAutoHeight(0);
          }
        }

        this.setStickyGallery();
      }

      handleGalleryResize() {
        const resolution = this.matchResolution();
        const autoHeightBand = this.matchAutoHeightBand();
        const resolutionChanged = resolution !== this.resolution;
        const autoHeightBandChanged = autoHeightBand !== this.autoHeightBand;

        if (resolutionChanged || autoHeightBandChanged) {
          this.resolution = resolution;
          this.autoHeightBand = autoHeightBand;

          switch (this.configuration.desktopLayout) {
            case "collage":
              this.handleCollage();
              break;
            case "masonry":
              this.handleMassonry();
              break;
            default:
              this.destroyCarouselGallery();
              this.initializeCarouselGallery();
              break;
          }

          this.setStickyGallery();
          this.refreshSwiperDimensions();
          this.syncProductPageLightboxState();
          return;
        }

        this.refreshSwiperDimensions();
        this.syncProductPageLightboxState();
      }

      syncProductPageLightboxState() {
        if (!this.isProductPage || !this.elements?.gallery) return;
        this.ensureGalleryExpandButtons();
        syncPhotoSwipeLinks(this.elements.gallery);
      }

      scheduleGalleryResize() {
        if (this._resizeRaf) return;
        this._resizeRaf = requestAnimationFrame(() => {
          this._resizeRaf = null;
          this.handleGalleryResize();
        });
      }

      constructor() {
        super();
        this._boundGalleryResize = () => this.scheduleGalleryResize();
      }

      connectedCallback() {
        this.isProductPage = this.dataset.productPage === "";

        this.galleryLoader = this.querySelector("#gallery-loader");
        this.renderedSlides = [];
        this.readConfiguration();
        this.initializeGallery();
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
        this.classList.add("loaded");

        this.setStickyGallery();
        this.ensureGalleryExpandButtons();
        this.bindProductPageLightbox();
        window.addEventListener("resize", this._boundGalleryResize);
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this._boundGalleryResize);
        if (this._boundProductPageLightboxClick) {
          this.removeEventListener("click", this._boundProductPageLightboxClick);
        }
        if (this._resizeRaf) {
          cancelAnimationFrame(this._resizeRaf);
          this._resizeRaf = null;
        }
      }

      setStickyGallery() {
        if (this.isProductPage) {
          const headerHeight =
            document.querySelector(".wt-header")?.offsetHeight;
          const swiperRect = this.getBoundingClientRect();

          const fitsInViewport =
            swiperRect.top >= 0 &&
            swiperRect.left >= 0 &&
            swiperRect.height <= window.innerHeight;

          const positionTopValue = `${headerHeight + 16}px`;

          this.style.setProperty("--position-top", positionTopValue);
          this.classList.add("wt-product__gallery--sticky", fitsInViewport);
        }
      }

      readConfiguration() {
        // general configuration
        this.configuration = [];

        this.elements = {
          section: this,
          gallery: this.querySelector("[data-gallery]"),
          thumbs: this.querySelector("[data-thumbs]"),
          galleryContainer: this.querySelector("[data-gallery]").querySelector(
            "[data-swiper-container]",
          ),
          thumbsContainer: this.querySelector("[data-thumbs]").querySelector(
            "[data-swiper-container]",
          ),
        };

        this.elements.gallerySlides = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));
        this.elements.thumbsSlides = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));

        const default_configuration = {
          sliderEnabledBreakpoint: 900,
          desktopLayout: "carousel-vertical",
        };

        const custom_configuration = JSON.parse(
          this.elements.section.querySelector("[data-configuration]").innerHTML,
        );
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
          sliderEnabledBreakpoint: Number(
            custom_configuration.sliderEnabledBreakpoint ??
              default_configuration.sliderEnabledBreakpoint,
          ),
        };

        let autoHeightEnabled = window.innerWidth <= 768;

        const UNMUTE_CLASS_TOGGLE = "wt-hero-video__sound-toggle--unmuted";

        function handleSlideVideos(swiper) {
          const allVideos = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll("video")),
          );
          const allToggleBtns = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll(".wt-hero-video__sound-toggle")),
          );

          // pause, mute & disable controls for all videos
          allVideos.forEach((video) => {
            video.pause();
            video.currentTime = 0;
            video.muted = true;
            video.controls = false;
            clearTimeout(video._hideCtrlsTimer);
          });

          // reset all toggle buttons to "off" state
          allToggleBtns.forEach((btn) => {
            btn.setAttribute("data-sound", "off");
            btn.classList.remove(UNMUTE_CLASS_TOGGLE);
          });

          // play & keep muted the active slide's video
          const activeSlide = swiper.slides[swiper.activeIndex];
          const activeVideo = activeSlide?.querySelector("video");
          if (activeVideo) {
            activeVideo.muted = true;
            activeVideo.play().catch((err) => {
              console.warn("Video playback failed:", err);
            });
          }
        }

        function initVideoInteractions(swiper) {
          handleSlideVideos(swiper);

          // add delegation listener for clicks
          swiper.el.addEventListener("click", (e) => {
            // Video element tapped -> show native controls temporarily
            const video = e.target.closest("video");
            if (video) {
              video.controls = true;
              video.blur();
              clearTimeout(video._hideCtrlsTimer);
              video._hideCtrlsTimer = setTimeout(() => {
                video.controls = false;
              }, 3000);
              return;
            }
          });
        }

        // gallery swiper configuration
        const default_gallery_configuration = {
          autoHeight: autoHeightEnabled,
          threshold: 10,
          grabCursor: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: {
            el: ".wt-slider__scrollbar",
            draggable: true,
          },
          pagination: {
            el: ".swiper-pagination",
            type: "fraction",
          },
          on: {
            afterInit: initVideoInteractions,
            slideChangeTransitionEnd: handleSlideVideos,
          },
        };

        const custom_gallery_configuration = JSON.parse(
          this.elements.gallery.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.gallery_configuration = {
          ...default_gallery_configuration,
          ...custom_gallery_configuration,
        };

        const updateThumbsSwiperClasses = (swiperInstance) => {
          const container = swiperInstance.el;
          const cls = {
            beginning: "swiper-at-beginning",
            end: "swiper-at-end",
            locked: "swiper-locked",
            ready: "swiper-thumbs-ready",
          };

          container.classList.remove(
            cls.beginning,
            cls.end,
            cls.locked,
            cls.ready,
          );

          if (swiperInstance.isLocked) {
            container.classList.add(cls.locked);
            return;
          }

          container.classList.add(cls.ready);

          if (swiperInstance.isBeginning)
            container.classList.add(cls.beginning);
          if (swiperInstance.isEnd) container.classList.add(cls.end);
        };

        // gallery thumbs swiper configuration
        const default_thumbs_configuration = {
          grabCursor: true,
          spaceBetween: 8,
          slidesPerView: 4,
          freeMode: false,
          threshold: 5,
          direction: "horizontal",
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          on: {
            afterInit(swiper) {
              updateThumbsSwiperClasses(swiper);
            },
            lock: updateThumbsSwiperClasses,
            unlock: updateThumbsSwiperClasses,
            slideChange: updateThumbsSwiperClasses,
            observerUpdate: updateThumbsSwiperClasses,
            resize: updateThumbsSwiperClasses,
          },
        };

        const custom_thumbs_configuration = JSON.parse(
          this.elements.thumbs.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.thumbs_configuration = {
          ...default_thumbs_configuration,
          ...custom_thumbs_configuration,
        };
        this._galleryOnHandlers = this.gallery_configuration.on;
        this._thumbsOnHandlers = this.thumbs_configuration.on;
        this._galleryConfigurationTemplate = JSON.parse(
          JSON.stringify(this.gallery_configuration),
        );
        this._thumbsConfigurationTemplate = JSON.parse(
          JSON.stringify(this.thumbs_configuration),
        );
        delete this._galleryConfigurationTemplate.on;
        delete this._thumbsConfigurationTemplate.on;
        this.resolution = this.matchResolution();
        this.autoHeightBand = this.matchAutoHeightBand();
      }

      initializeGallery() {
        switch (this.configuration.desktopLayout) {
          case "carousel-vertical":
          case "carousel-horizontal":
            this.initializeCarouselGallery();
            break;
          case "masonry":
            this.handleMassonry();
            break;
          case "collage":
            this.handleCollage();
            break;
        }
      }

      shouldInitializeThumbs() {
        if (!this.elements?.thumbs) return false;
        const layout = this.configuration.desktopLayout;
        if (layout === "collage" || layout === "masonry") {
          return this.elements.thumbs.hasAttribute("data-thumbs-mobile");
        }
        return true;
      }

      initializeCarouselGallery() {
        let thumbsSwiper = null;
        if (this.shouldInitializeThumbs()) {
          if (this.thumbsSwiper == null) {
            this.decorateSwiper(
              this.elements.thumbs,
              "wt-slider__container--thumbs",
            );
            this.thumbsSwiper = this.swiperThumbsInitilize();
          }
          thumbsSwiper = this.thumbsSwiper;
        }
        this.decorateSwiper(this.elements.gallery);
        this.swiperGalleryInitilize(thumbsSwiper);

        const disableTouchSlide =
          this.elements.gallery.querySelector(".disable-touch");
        if (disableTouchSlide) {
          disableTouchSlide.classList.add("swiper-no-swiping");
        }
      }

      buildGalleryConfiguration(thumbsSwiper) {
        const desktopRatio = this.getAttribute("desktop-ratio");
        const useAlwaysAutoHeight =
          (this.configuration.desktopLayout === "carousel-vertical" &&
            desktopRatio === "original") ||
          this.configuration.desktopLayout === "carousel-horizontal";
        const autoHeightEnabled = useAlwaysAutoHeight
          ? true
          : window.innerWidth <= 900;

        let config = {
          ...JSON.parse(JSON.stringify(this._galleryConfigurationTemplate)),
          on: this._galleryOnHandlers,
          autoHeight: autoHeightEnabled,
        };

        if (thumbsSwiper) {
          config = {
            ...config,
            thumbs: {
              swiper: thumbsSwiper,
            },
            autoHeight: autoHeightEnabled,
          };
        }

        if (
          this.isProductPage &&
          this.isMobileGallery() &&
          this.id.endsWith("2")
        ) {
          const scrollbar = config.scrollbar;
          config = {
            ...config,
            grabCursor: false,
            simulateTouch: false,
            preventClicks: true,
            preventClicksPropagation: true,
            scrollbar: scrollbar
              ? { ...scrollbar, draggable: false }
              : scrollbar,
          };
        }

        if (this.isMobileCollageCarousel()) {
          const paginationEl = this.elements.gallery.querySelector(
            ".wt-slider__pagination--gallery",
          );
          const { thumbs, navigation, scrollbar, ...mobileCollageConfig } =
            config;
          config = {
            ...mobileCollageConfig,
            slidesPerView: "auto",
            spaceBetween: 12,
            centeredSlides: false,
            slidesPerGroup: 1,
            autoHeight: false,
            grabCursor: true,
            pagination: paginationEl
              ? {
                  el: paginationEl,
                  type: "bullets",
                  clickable: true,
                }
              : false,
          };
        }

        return config;
      }

      buildThumbsConfiguration() {
        const autoHeightEnabled = window.innerWidth <= 900;
        return {
          ...JSON.parse(JSON.stringify(this._thumbsConfigurationTemplate)),
          on: this._thumbsOnHandlers,
          autoHeight: autoHeightEnabled,
        };
      }

      swiperGalleryInitilize(thumbsSwiper) {
        if (this.gallerySwiper == null) {
          this.gallerySwiper = new Swiper(
            this.elements.gallery,
            this.buildGalleryConfiguration(thumbsSwiper),
          );
        }
      }

      swiperThumbsInitilize() {
        return new Swiper(
          this.elements.thumbs,
          this.buildThumbsConfiguration(),
        );
      }

      destroyCarouselGallery() {
        if (this.thumbsSwiper != null) {
          this.thumbsSwiper.destroy();
          this.thumbsSwiper = null;
          this.undecorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
        }
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
        this.resetGalleryInlineDimensions();
        this.style.minHeight = "unset";
      }

      handleMassonry() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeMasonryGallery();
        } else {
          this.destroyMasonryGallery();
          this.destroyCarouselGallery();
          this.initializeCarouselGallery();
        }
      }

      handleCollage() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeCollageGallery();
        } else {
          this.destroyCollageGallery();
          this.destroyCarouselGallery();
          this.initializeCarouselGallery();
        }
      }

      decorateSwiper(el, element_class) {
        el.classList.add("swiper", "wt-slider__container");
        if (element_class) el.classList.add(element_class);
        el.querySelector("[data-swiper-container]")?.classList.add(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add("swiper-slide", "wt-slider__slide");
        });
        this.galleryUpdateEvent();
      }

      undecorateSwiper(el, element_class) {
        el.classList.remove("swiper", "wt-slider__container", "undefined");
        if (element_class) el.classList.remove(element_class);
        el.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
      }

      decorateCollage(el, element_class) {
        el.classList.add(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.add(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add(`wt-${element_class}__slide`);
        });
        this.galleryUpdateEvent();
      }

      undecorateCollage(el, element_class) {
        el.classList.remove(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.remove(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove(`wt-${element_class}__slide`);
        });
      }

      initializeCollageGallery() {
        this.decorateCollage(this.elements.gallery, "collage");
      }

      destroyCollageGallery() {
        this.undecorateCollage(this.elements.gallery, "collage");
      }

      initializeMasonryGallery() {
        this.decorateCollage(this.elements.gallery, "masonry");
      }

      destroyMasonryGallery() {
        this.undecorateCollage(this.elements.gallery, "masonry");
      }

      sortSlides(slides, featured_media_id) {
        // Find the index of the slide with featured_media_id
        const featuredIndex = slides.findIndex((slide) => {
          const mediaId = slide.querySelector("img")
            ? Number(slide.querySelector("img").getAttribute("data-media-id"))
            : null;
          return mediaId === Number(featured_media_id);
        });

        // If a slide with featured_media_id is found, move it to the beginning of the array
        if (featuredIndex > -1) {
          const featuredSlide = slides[featuredIndex];
          slides.splice(featuredIndex, 1);
          slides.unshift(featuredSlide);
        }

        return slides;
      }

      beforeGalleryChange() {
        this.classList.add("loading");
        this.galleryLoader?.classList.remove("hidden");
        this.style.minHeight = `${this.offsetHeight}px`;
      }

      afterGalleryChange() {
        this.style.minHeight = "unset";
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
      }

      get swiper() {
        return this.gallerySwiper;
      }

      ensureFlavourSlideStash() {
        if (!this._flavourGalleryStash) {
          this._flavourGalleryStash = document.createElement("div");
          this._flavourGalleryStash.hidden = true;
          this._flavourGalleryStash.setAttribute("aria-hidden", "true");
          this.appendChild(this._flavourGalleryStash);
        }
        if (!this._flavourThumbsStash) {
          this._flavourThumbsStash = document.createElement("div");
          this._flavourThumbsStash.hidden = true;
          this._flavourThumbsStash.setAttribute("aria-hidden", "true");
          this.appendChild(this._flavourThumbsStash);
        }
      }

      getFlavourSlidesForRegion(regionKey) {
        this.ensureFlavourSlideStash();

        return Array.from(this.querySelectorAll("[data-swiper-slide]")).filter(
          (slide) => {
            if (!slide.dataset.flavourGalleryRegion) {
              slide.dataset.flavourGalleryRegion = slide.closest("[data-thumbs]")
                ? "thumbs"
                : "gallery";
            }
            return slide.dataset.flavourGalleryRegion === regionKey;
          },
        );
      }

      syncFlavourSlidesInRegion(regionKey, wrapper, stash, swiperInstance) {
        if (!wrapper) return 0;

        const slides = this.getFlavourSlidesForRegion(regionKey);
        let visibleCount = 0;

        slides.forEach((slide) => {
          const hidden = isGallerySlideHidden(slide);
          if (hidden) {
            if (slide.parentElement !== stash) stash.appendChild(slide);
            return;
          }

          visibleCount += 1;
          if (slide.parentElement !== wrapper) wrapper.appendChild(slide);
        });

        if (swiperInstance) {
          swiperInstance.slideTo(0, 0);
          swiperInstance.update();
          swiperInstance.pagination?.render?.();
          swiperInstance.pagination?.update?.();
        }

        return visibleCount;
      }

      refreshFlavourSlides() {
        if (!this.elements?.gallery) return;

        this.ensureFlavourSlideStash();

        const galleryWrapper = this.elements.galleryContainer;
        const galleryVisible = this.syncFlavourSlidesInRegion(
          "gallery",
          galleryWrapper,
          this._flavourGalleryStash,
          this.gallerySwiper,
        );

        if (this.shouldInitializeThumbs()) {
          this.syncFlavourSlidesInRegion(
            "thumbs",
            this.elements.thumbsContainer,
            this._flavourThumbsStash,
            this.thumbsSwiper,
          );
        }

        if (galleryVisible > 0 && this.elements.gallery) {
          this.elements.gallery.classList.remove(
            "wt-product__gallery--even",
            "wt-product__gallery--odd",
          );
          this.elements.gallery.classList.add(
            galleryVisible % 2 === 0
              ? "wt-product__gallery--even"
              : "wt-product__gallery--odd",
          );
        }

        syncPhotoSwipeLinks(this.elements.gallery);
        this.ensureGalleryExpandButtons();
      }

      ensureGalleryExpandButtons() {
        const galleryEl = this.elements?.gallery;
        if (!galleryEl) return;

        if (
          !this.isProductPage ||
          this.id.endsWith("2") ||
          isProductPageLightboxDisabled(galleryEl)
        ) {
          galleryEl
            .querySelectorAll(".hz-gallery-expand")
            .forEach((button) => button.remove());
          return;
        }

        galleryEl.classList.add("photoswipe");

        Array.from(galleryEl.querySelectorAll("[data-swiper-slide]")).forEach(
          (slide) => {
            const expandButton = slide.querySelector(".hz-gallery-expand");
            if (isGallerySlideHidden(slide) || !isPdpLightboxSlide(slide, galleryEl)) {
              expandButton?.remove();
              return;
            }

            const img = slide.querySelector("img.wt-product__img");
            if (!img) {
              expandButton?.remove();
              return;
            }

            ensureZoomLink(slide, img);

            if (expandButton) return;

            const button = document.createElement("button");
            button.type = "button";
            button.className = "hz-gallery-expand swiper-no-swiping";
            button.setAttribute("aria-label", "View image larger");
            button.innerHTML = GALLERY_EXPAND_ICON;
            button.addEventListener("click", (event) => {
              event.preventDefault();
              event.stopPropagation();
              openProductPageLightbox(this, slide);
            });
            slide.appendChild(button);
          },
        );

        syncPhotoSwipeLinks(galleryEl);
      }

      bindProductPageLightbox() {
        if (
          !this.isProductPage ||
          this.id.endsWith("2") ||
          isProductPageLightboxDisabled(this.elements?.gallery)
        ) {
          return;
        }

        this._boundProductPageLightboxClick = (event) => {
          if (
            event.target.closest(
              ".hz-gallery-expand, .wt-slider__nav-btn, .wt-slider__pagination, .wt-slider__pagination--gallery, .swiper-pagination",
            )
          ) {
            return;
          }

          const slide = event.target.closest("[data-swiper-slide]");
          if (!slide || !this.elements?.gallery?.contains(slide)) return;
          if (!isPdpLightboxSlide(slide, this.elements.gallery)) return;
          if (isGallerySlideHidden(slide)) return;
          if (!slide.querySelector("img.wt-product__img, img:not([aria-hidden='true'])"))
            return;

          if (this.gallerySwiper?.allowClick === false) return;

          event.preventDefault();
          openProductPageLightbox(this, slide);
        };

        this.addEventListener("click", this._boundProductPageLightboxClick);
      }

      filterSlidesByOptions(
        slides,
        options,
        featured_media_id,
        matchAll = true,
      ) {
        const lowercaseOptions = options.map((option) =>
          option.toLowerCase().replace(/\s/g, ""),
        );
        const anyTagged = slides.some((slide) => {
          let media = slide.querySelector("img");
          if (media == null) media = slide?.querySelector("video");
          const alt = media ? media.getAttribute("alt") : "";
          return (alt?.match(/#[^\s#]+/g) || []).length > 0;
        });

        return slides.filter((slide) => {
          let media = slide.querySelector("img");
          if (media == null) media = slide?.querySelector("video");
          const alt = media ? media.getAttribute("alt") : "";
          const mediaId = media ? media.getAttribute("data-media-id") : "";

          if (String(mediaId) === String(featured_media_id)) return true;

          const altHashtags = (alt?.match(/#[^\s#]+/g) || []).map((hashtag) =>
            hashtag.slice(1).toLowerCase(),
          );

          if (altHashtags.length === 0) return !anyTagged;
          if (altHashtags.some((tag) => tag.split("|").includes("all")))
            return true;

          if (matchAll) {
            return altHashtags.every((tag) =>
              tag
                .split("|")
                .some((variant) => lowercaseOptions.includes(variant.trim())),
            );
          } else {
            return altHashtags.some((tag) =>
              tag
                .split("|")
                .some((variant) => lowercaseOptions.includes(variant.trim())),
            );
          }
        });
      }

      galleryUpdateEvent(opt) {
        const galleryEvent = new CustomEvent("gallery:updated", {
          bubbles: true,
          cancelable: true,
          detail: {
            desc: "gallery updated",
            selector: ".wt-product__gallery",
            ...opt,
          },
        });
        document.dispatchEvent(galleryEvent);
      }

      filterSlides(options, featured_media_id, matchAll = true, callback) {
        if (!this.elements) return;
        const originalGallerySlides = Array.from(
          this.elements.gallerySlides,
          (el) => el.cloneNode(true),
        );
        const originalThumbsSlides = Array.from(
          this.elements.thumbsSlides,
          (el) => el.cloneNode(true),
        );

        const getMediaId = (item) => item?.dataset?.mediaId;

        let filteredGallerySlides = this.filterSlidesByOptions(
          originalGallerySlides,
          options,
          featured_media_id,
          matchAll,
        );
        let filteredThumbsSlides = this.filterSlidesByOptions(
          originalThumbsSlides,
          options,
          featured_media_id,
          matchAll,
        );

        if (filteredGallerySlides.length === 0) {
          filteredGallerySlides = [...originalGallerySlides];
          filteredThumbsSlides = [...originalThumbsSlides];
        }

        if (featured_media_id) {
          filteredGallerySlides = this.sortSlides(
            filteredGallerySlides,
            featured_media_id,
          );
          filteredThumbsSlides = this.sortSlides(
            filteredThumbsSlides,
            featured_media_id,
          );
        }

        const renderedSlidesChanged =
          this.renderedSlides.map(getMediaId).toString() !==
          filteredGallerySlides.map(getMediaId).toString();

        if (this.gallerySwiper && this.thumbsSwiper && renderedSlidesChanged) {
          this.beforeGalleryChange();
          const thumbSlidesWrapper = this.querySelector(
            "[data-thumbs] [data-swiper-container]",
          );
          const gallerySlidesWrapper = this.querySelector(
            "[data-gallery] [data-swiper-container]",
          );

          // Assuming this.gallerySwiper is the swiper instance for the main gallery
          this.gallerySwiper.removeAllSlides();
          gallerySlidesWrapper.innerHTML = "";
          const swiperContainer = this.querySelector(".swiper-wrapper");
          filteredGallerySlides.forEach((slide, idx) => {
            if (idx === 0) {
              const slideImg = slide.querySelector("img");
              if (slideImg)
                slideImg.onload = function () {
                  swiperContainer.style.height = "auto";
                };
            }
            this.gallerySwiper.appendSlide(slide);
          });
          this.decorateSwiper(this.elements.gallery);

          // Assuming this.thumbsSwiper is the swiper instance for the thumbnails
          this.thumbsSwiper.removeAllSlides();
          thumbSlidesWrapper.innerHTML = "";

          filteredThumbsSlides.forEach((slide) =>
            this.thumbsSwiper.appendSlide(slide),
          );

          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );

          this.thumbsSwiper.update();
          this.gallerySwiper.update();
        } else if (
          this.configuration.desktopLayout == "collage" ||
          this.configuration.desktopLayout == "masonry"
        ) {
          if (renderedSlidesChanged) {
            this.beforeGalleryChange();
            // Remove all existing slides
            this.elements.gallery
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());
            this.elements.thumbs
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());

            filteredGallerySlides.forEach((slide) =>
              this.elements.gallery
                .querySelector("[data-swiper-container]")
                .append(slide),
            );
            filteredThumbsSlides.forEach((slide) =>
              this.elements.thumbs
                .querySelector("[data-swiper-container]")
                .append(slide),
            );

            this.decorateCollage(
              this.elements.gallery,
              this.configuration.desktopLayout,
            );

            // add wt-product__gallery --even --odd classes to MediaGallery container depends on number of slides
            this.elements.gallery.classList.remove("wt-product__gallery--even");
            this.elements.gallery.classList.remove("wt-product__gallery--odd");
            if (filteredGallerySlides.length % 2 == 0) {
              this.elements.gallery.classList.add("wt-product__gallery--even");
            } else {
              this.elements.gallery.classList.add("wt-product__gallery--odd");
            }
          }
        }

        if (featured_media_id) {
          this.setActiveMedia(featured_media_id, true);
        } else {
          this.setActiveMedia(
            filteredGallerySlides[0]
              ?.querySelector("img")
              ?.getAttribute("data-media-id"),
            true,
          );
        }
        this.gallerySwiper?.update();

        if (renderedSlidesChanged) {
          setTimeout(this.afterGalleryChange.bind(this), 300);
        }

        this.renderedSlides = [...filteredGallerySlides];
        this.ensureGalleryExpandButtons();
      }

      setActiveMedia(mediaId, prepend) {
        let media = this.elements.gallery.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.gallerySwiper != null) {
          this.gallerySwiper.slideTo(this.indexInParent(media));
          if (this.thumbsSwiper != null) {
            this.thumbsSwiper.slideTo(this.indexInParent(media));
          }
        }
      }

      matchResolution() {
        if (window.innerWidth < this.getSliderBreakpoint()) {
          return "mobile";
        }
        return "desktop";
      }

      indexInParent(node) {
        if (!node) return -1;
        let children = node.parentNode.childNodes;
        let num = 0;
        for (let i = 0; i < children.length; i++) {
          if (children[i] == node) return num;
          if (children[i].nodeType == 1) num++;
        }
        return -1;
      }
    },
  );
}
